// uncomment below line when ng serve
import { ActivatedRoute, Router } from '@angular/router';

// Code needed for serve or web components
//add inject to this import for the changes to the form building
//see validate against dynamic list in srp angular standard validate against dynamic list
import { Component, inject, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { TrpcService } from '../../services/trpc/trpc.service';
import { MessageService } from 'primeng/api';
import { TennatAccount } from '../../../../../express-tRPC/src/api/tenants/types-generic';
import { Title } from '@angular/platform-browser';
import { SUREPARKgroup } from '../../../../../express-tRPC/src/api/surepark/types';
import { WPSCompany } from '../../../../../express-tRPC/src/entity/wps-companies';
import { InvoiceService } from '../../services/invoice/invoice.service';
import { combineLatest, finalize } from 'rxjs';
import { CommonSharedService } from 'src/app/shared/services/common-shared.service';
import { State } from '../../../../../express-tRPC/src/shared/types';
import { dropdownOption } from 'src/app/types/common';
import { NewSureparkGroupFormComponent } from 'src/app/components/new-surepark-group-form/new-surepark-group-form.component';

export interface tQueryParams {
  suite: string,
  account: string
}

@Component({
  selector: 'tenant-page',
  templateUrl: './tenant-update-page.component.html',
  styleUrl: './tenant-update-page.component.scss'
})
export class TenantUpdatePageComponent {

  queryParams!: tQueryParams;

  @Input() suite = '';
  @Input() account = '';

  tenantDetails: TennatAccount | null = null;
  states: State[] = [];
  sureparkGroupList: SUREPARKgroup[] = [];
  WPSCompaniesList: WPSCompany[] = [];
  invoiceBatchOptions: State[] = [{ name: 'Print', code: 'print' }, { name: 'Email', code: 'email' }];

  loadingTenantDetails: boolean = false;

  private fb = inject(FormBuilder);
  zipCodeRegex = /^(?:\d{5}|\d{5}-\d{4})$/;

  tenantForm = this.fb.group({
    suite: [{ value: '', disabled: true }, []],
    account: [{ value: '', disabled: true }, []],
    tax_code: [""],
    name: ["", [Validators.required]],
    new_company: [false, []],
    wps_company: [""],
    // billto: [""],
    email: ["", [this.commonSharedService.multiEmailValidator]],
    address1: ["", [Validators.required]],
    address2: ["", []],
    city: ["", [Validators.required]],
    state: [""],
    zip: ["", [Validators.required, Validators.pattern(this.zipCodeRegex)]],
    invoice_delivery: [""]
  },);

  
  lotIdOptions: dropdownOption[] = [];
  timezoneGroupOptions: dropdownOption[] = [];

  showNewGroupDialog: boolean = false;
  newSureparkGroupData: any = undefined;

  //showDevPanel: boolean = true;
  saveTenantRequest: any = {};
  currentLocation: string = '';

  tenantContacts: any[] = [];
  loadingTenantContacts: boolean = false;

  tenantUpdating: boolean = false;
  formSubmitted: boolean = false;

  @ViewChild(NewSureparkGroupFormComponent) newSureparkGroupFormComponent!: NewSureparkGroupFormComponent;
  
  constructor(
    // uncomment below line when ng serve
    private commonSharedService: CommonSharedService,
    public route: ActivatedRoute,
    // Code needed for serve or web components
    private messageService: MessageService,
    private trpcService: TrpcService,
    private titleService: Title,
    private invoiceService: InvoiceService,
  ) { }

  ngOnInit(): void {
    this.loadingTenantDetails = true;

    combineLatest([this.trpcService.currentLocationObservable, this.route.params]).subscribe(([result1, result2]) => {
      if(result1 && result2) {
        this.currentLocation = result1;

        this.queryParams = result2 as tQueryParams;
        console.log("Query Parameters" + this.queryParams);

        this.titleService.setTitle(`Update Tenant Info | ${this.queryParams.suite}-${this.queryParams.account} Loc ${this.currentLocation}`);
        
        this.getTenant(this.queryParams);
        // this.getContacts();
      }
    });

  }

  loadTenantDataInForm(tenantObject: TennatAccount) {
    const [address1, address2] = tenantObject.billtoaddress.split("<br>");

    //
    // We need to determine what the company name is for ParkPro because it relates to the SUREPARK group.
    // This determines which SUREPARK contacts will appear in this ParkPro tenant.
    //
    let sureparkGroupID = '';
    if (tenantObject.parkingDeviceGroupID) {
      sureparkGroupID = tenantObject.parkingDeviceGroupID;
      console.log(`Using existing ${tenantObject.parkingDeviceGroupID} for group`);
    } else {
      sureparkGroupID = this.getGroupIdForTenant(tenantObject.billto);
      // console.log(`Resolved ${tenantObject.billto} to ${parkProCompanyName} for group`);
    }

    this.tenantForm.setValue({
      account: tenantObject.accountNumber,
      suite: tenantObject.suite,
      tax_code: tenantObject.nonTaxCode,
      name: tenantObject.billto,
      new_company: false,
      wps_company: sureparkGroupID,
      // billto: tenantObject.billtocontact,
      email: tenantObject.billtoemail,
      address1: address1 || '',
      address2: address2 || '',
      city: tenantObject.billtocity,
      state: tenantObject.billtostate,
      zip: tenantObject.billtozip,
      invoice_delivery: tenantObject.batchinvoice
    });
  }

  /**
   * Given a ParkPro bill to name determine if there is a matching SUREPARK group name.
   * If there is a match then return the SUREPARK group ID.
   * If there is no match then return an empty string.
   * @param billTo
   */
  getGroupIdForTenant(billTo: string): string {
    const matchingGroup = this.sureparkGroupList.find((group: SUREPARKgroup) => group.name == billTo);
    return matchingGroup?.id || '';
  }

  async loadStatesList() {
    let statesList: State[] | void = await this.trpcService.trpc.lots.getStates.query().catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    this.states = statesList || [];
  }

  /**
   * Retrieve the list of SUREPARK group objects for selection in the dropdown list.
   */
  async loadSureparkGroups() {
    let sureparkGroupList: SUREPARKgroup[] | void = await this.trpcService.trpc.surepark.getSureparkGroups.query().catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    this.sureparkGroupList = [...(sureparkGroupList || [])];
  }

  async loadWPSCompanies() {
    let WPSCompaniesList: WPSCompany[] | void = await this.trpcService.trpc.surepark.getWPSCompanies.query().catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    this.WPSCompaniesList = WPSCompaniesList || [];
  }

  async getSettings() {
    let settings: any = await this.trpcService.trpc.settings.getSetting.mutate({settingName: ['lots', 'timezonegroups']}).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    if(settings) {
      this.lotIdOptions = settings?.lots || [];
      this.timezoneGroupOptions = settings?.timezonegroups || [];

    }
  }

  async getTenant(sqlParams: tQueryParams) {

    this.loadingTenantDetails = true;

    await this.loadStatesList();
    await this.loadSureparkGroups();
    await this.getSettings();
    // await this.loadWPSCompanies();

    let tenantDetails: TennatAccount | null | void = await this.trpcService.trpc.tenant_list.getTenantDetails.query(sqlParams).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
      this.loadingTenantDetails = false;
    });

    if (tenantDetails) {
      this.tenantDetails = tenantDetails;
      this.loadTenantDataInForm(tenantDetails)
      this.loadingTenantDetails = false;
    }

  }

  getContacts(): void {

    this.loadingTenantContacts = true;

    this.invoiceService.getTenantContacts(this.currentLocation, this.queryParams.suite, this.queryParams.account)
      .pipe(finalize(() => { this.loadingTenantContacts = false; }))
      .subscribe((data: any) => {
        if (data) {
          this.tenantContacts = data;
        }

      });
  }

  displayNewGroupDialog() {

    this.showNewGroupDialog = true;
    setTimeout(() => {
      
      this.newSureparkGroupFormComponent.resetForm();

      if(this.tenantForm.value['new_company'] && this.newSureparkGroupData) {
        this.newSureparkGroupFormComponent.setFormValues(this.newSureparkGroupData);
      } else {
        //Tenant Name should be Default Group Name
        this.newSureparkGroupData = {
          name: this.tenantForm.controls['name'].value || '',
          cardFunction: '',
          antiPassback: '',
          stations: [],
          schedules: []
        }

        this.newSureparkGroupFormComponent.setFormValues(this.newSureparkGroupData);
      }
    }, 0);
  }

  closeNewGroupDialog() {
    this.showNewGroupDialog = false;
  }

  clearNewGroupDetails() {
    
    this.tenantForm.get('new_company')?.setValue(false);
    this.tenantForm.get('new_company')?.updateValueAndValidity();
    
    this.newSureparkGroupFormComponent.resetForm();

    const newGroupIndex = this.sureparkGroupList.findIndex((group) => group.id == '');
    if(newGroupIndex !== -1) {
      //remove new group from existing surepark group list
      this.sureparkGroupList.splice(newGroupIndex, 1);

      //Clear existing selected group
      this.tenantForm.controls.wps_company.reset();
      this.tenantForm.controls.wps_company.updateValueAndValidity();
    }

    this.closeNewGroupDialog();
  }

  saveNewGroupDetails() {

    const isValid = this.newSureparkGroupFormComponent.validateForm();

    if(!isValid) {
      this.messageService.add({
        severity: 'error',
        detail: 'Please fill required fields.'
      });
      return;
    }

    this.tenantForm.get('new_company')?.setValue(true);
    this.tenantForm.get('new_company')?.updateValueAndValidity();
    
    this.newSureparkGroupData = this.newSureparkGroupFormComponent.getValues();
    
    this.tenantForm.controls.wps_company.reset();
    this.tenantForm.controls.wps_company.updateValueAndValidity();
    
    const newGroup: SUREPARKgroup = {
      "id": '', //SET any string because we disn't saved group yet
      "name": this.newSureparkGroupData['name'],
      "antiPassback": this.newSureparkGroupData['antiPassback'],
      "cardFunction": this.newSureparkGroupData['cardFunction'],
      "vaultAccess": false,
      "kioskAccess": false,
      "defaultGroup": false,
      "groupTimeZoneGroups": this.newSureparkGroupData['schedules'],
      "groupStations": this.newSureparkGroupData['stations']
    };

    const newGroupIndex = this.sureparkGroupList.findIndex((group) => group.id == newGroup.id);
    
    if(newGroupIndex === -1) {
      //push new group into existing surepark group list
      this.sureparkGroupList = [...this.sureparkGroupList, newGroup];
    } else {
      this.sureparkGroupList[newGroupIndex] = newGroup;
    }
    
    this.tenantForm.controls.wps_company.setValue(newGroup.id);
    this.tenantForm.controls.wps_company.updateValueAndValidity();

    this.showNewGroupDialog = false;

  }

  async saveTenantData(): Promise<void> {
    this.messageService.clear();

    this.formSubmitted = true;
    if(this.tenantForm.invalid) {
      this.messageService.add({
        severity: 'error',
        detail: 'Please fill required fields.'
      });
      return;
    }

    this.formSubmitted = false;
    this.tenantUpdating = true;

    this.saveTenantRequest = this.tenantForm.getRawValue();
    if(this.saveTenantRequest['new_company']) {
      const newSureparkGroupDetails = structuredClone(this.newSureparkGroupData);
      this.saveTenantRequest['newSureparkGroupDetails'] = structuredClone(this.newSureparkGroupData);
      this.saveTenantRequest['newSureparkGroupDetails']['stations'] = [];
      if(newSureparkGroupDetails['stations']) {
        const selectedStation = this.lotIdOptions.find((lot: any) => newSureparkGroupDetails['stations']?.includes(lot.id));
        if(selectedStation) {
          this.saveTenantRequest['newSureparkGroupDetails']['stations'].push(selectedStation);
        }
      }
    }

    this.trpcService.trpc.tenant_list.saveTenantDetails.mutate(this.saveTenantRequest).then((response: any) => {
      this.messageService.add({
        severity: "success",
        detail: "Tenant has been saved.",
      });
      this.tenantUpdating = false;
      
      this.tenantForm.get('new_company')?.setValue(false);
      this.tenantForm.get('new_company')?.updateValueAndValidity();
      
      this.newSureparkGroupFormComponent.resetForm();
      this.newSureparkGroupData = undefined;

      // Reload SUREPARK groups to update availability status
      this.loadSureparkGroups();
    })
      .catch((error: any) => {
        // Display a more user-friendly error message for SUREPARK group conflicts
        let errorMessage = error.message;
        if (error.message.includes('SUREPARK group is already assigned')) {
          errorMessage = error.message;
        }
        this.messageService.add({
          severity: "error",
          detail: errorMessage,
        });
        this.tenantUpdating = false;
        // Reload SUREPARK groups to update availability status
        this.loadSureparkGroups();
      });

  }
}
